import ApplicationController from './application_controller'

/* Givsum 3.0 vanilla-JS bid submit controller for the item detail page.
 * Reuses the existing BidReflex (server logic unchanged) — only the DOM
 * glue is rewritten without jQuery. Kept separate from bid_controller.js
 * so the legacy auction pages are untouched.
 */
export default class extends ApplicationController {
  /* The winner/outbid alerts are siblings of the <form> (not descendants), so we
   * look them up via the form's parent — the "#bid_form_..." wrapper that CableReady
   * replaces wholesale on every bid/cancel. Stimulus re-runs connect() automatically
   * each time that replacement happens, so this keeps the alert in sync without
   * needing the old DOMContentLoaded + jQuery one-time init.
   */
  connect() {
    super.connect()

    if (!window.givsum_user_id) return

    // The Buy Now confirm button lives in a modal rendered via
    // `content_for :bootstrap_modal_content`, which the layout yields at the end of
    // <body> — outside this <form>'s subtree — so a Stimulus data-action on it can never
    // dispatch back to this controller. Bind a plain listener here instead. Modal ids are
    // scoped per item (#buyNowConfirmModal_<id>) since my_bids_card_v3 can render many
    // bid forms — and therefore many modals — on one page; the show-item page only ever
    // has one, but uses the same scoped id for a single consistent lookup. Independent of
    // the winner/loser alert guard below — my_bids_card_v3 has no such alerts at all, so
    // this must not be gated on their presence. Guard against re-binding a duplicate
    // listener on every connect() (the modal node survives CableReady form replacements).
    const auctionItemIdField = this.element.querySelector('input[name="auction_item_id"]')
    const auctionItemId = auctionItemIdField && auctionItemIdField.value
    if (auctionItemId) {
      const confirmBtn = document.querySelector(`#buyNowConfirmModal_${auctionItemId} .js-confirm-buy-now`)
      if (confirmBtn && confirmBtn.dataset.auctionBidBound !== 'true') {
        confirmBtn.dataset.auctionBidBound = 'true'
        confirmBtn.addEventListener('click', (e) => this.placeBid(e.target, true))
      }
    }

    const wrapper = this.element.parentElement
    if (!wrapper) return

    const winnerEl = wrapper.querySelector('.bidder_is_current_winner')
    const loserEl = wrapper.querySelector('.bidder_is_not_current_winner')
    const winningField = this.element.querySelector('input[name="winning_user_ids"]')
    if (!winnerEl || !loserEl || !winningField) return

    // `winning_user_ids` is rendered from a Ruby Array via `value:`, which Rails
    // serializes with Array#to_s (e.g. "[123, 456]"), not a space-separated string —
    // extracting digit runs works regardless of the exact bracket/comma formatting.
    const winningUserIds = (winningField.value.match(/\d+/g) || []).map(Number)
    const isWinning = winningUserIds.includes(window.givsum_user_id)

    // The broadcast-replace render (BidReflex/CancelBidReflex) always renders this
    // partial with current_user: nil, so its Ruby-side conditionals leave both alerts
    // textless — guarding on textContent here means those shared broadcasts stay
    // fully hidden instead of flashing an empty alert box at every other viewer.
    winnerEl.classList.toggle('d-none', !isWinning || !winnerEl.textContent.trim())
    loserEl.classList.toggle('d-none', isWinning || !loserEl.textContent.trim())

    // Old auction_item_controller.js disabled the submit button client-side whenever a live
    // CableReady update showed the current user already winning (getElementById('place_bid_button')
    // .disabled = true). BidReflex/CancelBidReflex always broadcast this form with
    // disable_bidding: false (it's rendered once and shared with every viewer), so without this
    // the button stays clickable after a live win/loss until a manual reload.
    const submitBtn = this.element.querySelector('[data-action*="auction-bid#submit"]')
    if (submitBtn) submitBtn.disabled = isWinning

    // Keep the +/- stepper in sync with the submit button — no point leaving it open to
    // click once you can't actually submit a bid with it.
    this.element.querySelectorAll('[data-action*="bid-counter#decrement"], [data-action*="bid-counter#increment"]')
      .forEach((btn) => { btn.disabled = isWinning })

    // Hide the Buy Now trigger once this viewer has already bought via buy-now — matches
    // old auction_item_controller.js's buyNowUserIds check. Same "shared broadcast can't
    // know who's watching" reasoning as isWinning above. Only relevant on the show-item
    // page (_bid_form_v3) — my_bids_card_v3 computes this server-side instead, since it's
    // always rendered fresh per-bidder rather than broadcast shared to every viewer.
    // .buy_now_button is a SIBLING of the <form> (both children of the wrapper), not a
    // descendant of it — querying via `wrapper`, not `this.element`, is required here.
    const buyNowUserIdsField = this.element.querySelector('input[name="buy_now_user_ids"]')
    if (buyNowUserIdsField) {
      const buyNowUserIds = (buyNowUserIdsField.value.match(/\d+/g) || []).map(Number)
      const alreadyBoughtByMe = buyNowUserIds.includes(window.givsum_user_id)
      wrapper.querySelectorAll('.buy_now_button').forEach((btn) => {
        btn.classList.toggle('d-none', alreadyBoughtByMe)
      })
    }
  }

  submit(event) {
    event.preventDefault()
    this.placeBid(event.target, false)
  }

  placeBid(triggerEl, buyNow) {
    triggerEl.disabled = true
    document.body.classList.add('wait')

    const form = this.element

    if (!window.givsum_user_id) {
      triggerEl.disabled = false
      document.body.classList.remove('wait')
      return
    }

    const bidderField = form.querySelector('input[name="bidder_id"]')
    if (bidderField) {
      bidderField.value = window.givsum_user_id
    }

    const buyNowField = form.querySelector('input[name="buy_now"]')
    if (buyNowField) {
      buyNowField.value = buyNow
    }

    // Captured BEFORE stimulate() so it reflects state prior to this bid — the server's
    // UserChannel broadcast (which also refreshes userBidsMap) can race this promise
    // resolving, so checking afterward would sometimes see the map already updated and
    // wrongly skip the increment. This client-side +1 doesn't depend on that broadcast
    // arriving at all, which the tab-count text_content push turned out not to reliably.
    const auctionItemIdField = form.querySelector('input[name="auction_item_id"]')
    const alreadyHadBidOnThisItem = hasExistingBidOnItem(auctionItemIdField && auctionItemIdField.value)

    this.stimulate('BidReflex#bid').then(() => {
      document.body.classList.remove('wait')
      if (!alreadyHadBidOnThisItem) incrementMyBidsTabCount()
    }).catch(() => {
      document.body.classList.remove('wait')
      triggerEl.disabled = false
    })
  }
}

function hasExistingBidOnItem(auctionItemId) {
  if (!auctionItemId) return true // fail-safe: unknown state, don't risk a wrong increment
  try {
    const myBidsMap = JSON.parse(localStorage.getItem('userBidsMap') || '{}')
    return Boolean(myBidsMap[auctionItemId])
  } catch (e) {
    return true
  }
}

function incrementMyBidsTabCount() {
  const countEl = document.querySelector('#my-bids-tab .tab-count')
  if (!countEl) return
  const match = countEl.textContent.match(/\d+/)
  const current = match ? parseInt(match[0], 10) : 0
  countEl.textContent = `(${current + 1})`
}
